import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Settings, Search, Save, RefreshCw, ChevronRight, Globe, Sliders, Check } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  overview: 'Overview', tools: 'Tools', lead_generation: 'Lead Generation', sales: 'Sales',
  marketing: 'Marketing', retention: 'Retention', service: 'Customer Service',
  hr: 'Human Resources', operations: 'Operations', website: 'Website', analytics: 'Analytics',
  settings: 'Settings',
};

function SettingField({ setting, settingKey, value, onChange }) {
  const { type, label, options } = setting;
  if (type === 'boolean') {
    return (
      <div className="flex items-center justify-between py-2">
        <div><Label className="text-xs">{label}</Label></div>
        <Switch checked={!!value} onCheckedChange={v => onChange(settingKey, v)} />
      </div>
    );
  }
  if (type === 'dropdown') {
    return (
      <div className="py-2">
        <Label className="text-xs">{label}</Label>
        <Select value={String(value || '')} onValueChange={v => onChange(settingKey, v)}>
          <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{(options || []).map(o => <SelectItem key={o} value={o}>{String(o).replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (type === 'number') {
    return (
      <div className="py-2">
        <Label className="text-xs">{label}</Label>
        <Input type="number" value={value ?? ''} onChange={e => onChange(settingKey, parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" />
      </div>
    );
  }
  return (
    <div className="py-2">
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ''} onChange={e => onChange(settingKey, e.target.value)} className="mt-1 h-8 text-sm" />
    </div>
  );
}

function ModuleSettingsCard({ moduleId, onClose }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/settings/modules/${moduleId}`)
      .then(r => setConfig(r.data))
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }, [moduleId]);

  const handleChange = (key, val) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: { ...prev.settings[key], value: val } }
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(config.settings).forEach(([k, s]) => { payload[k] = s.value; });
      await axios.put(`${API}/api/settings/modules/${moduleId}`, payload);
      toast.success(`${config.label || moduleId} settings saved`);
      setDirty(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;
  if (!config || !config.settings) return <p className="text-xs text-gray-400 p-4">No configurable settings for this module.</p>;

  const settingEntries = Object.entries(config.settings);
  return (
    <Card className="bg-white border-gray-border" data-testid={`module-settings-${moduleId}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sliders className="h-4 w-4 text-emerald-500" /> {config.label || moduleId}
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">{settingEntries.length} settings</Badge>
          </CardTitle>
          <div className="flex gap-2">
            {dirty && <Button size="sm" className="h-7 text-xs bg-emerald-brand hover:bg-emerald-600 gap-1" onClick={handleSave} disabled={saving}>{saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save</Button>}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onClose}>Close</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="divide-y divide-gray-50">
        {settingEntries.map(([key, setting]) => (
          <SettingField key={key} settingKey={key} setting={setting} value={setting.value} onChange={handleChange} />
        ))}
      </CardContent>
    </Card>
  );
}

export default function SettingsEnginePage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeModule, setActiveModule] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const fetchModules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/modules/registry`);
      setModules(data.modules || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const categories = ['all', ...new Set(modules.map(m => m.category).filter(Boolean))];
  const filtered = modules.filter(m => {
    if (activeCategory !== 'all' && m.category !== activeCategory) return false;
    if (search) return m.name.toLowerCase().includes(search.toLowerCase()) || m.module_id.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-engine-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Configuration Assistant<PageInfoTooltip pageKey="settings-engine" /></h1>
        <p className="text-gray-medium text-sm">Configure each module's behaviour, defaults, and automation rules</p>
      </div>

      {activeModule ? (
        <ModuleSettingsCard moduleId={activeModule} onClose={() => setActiveModule(null)} />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search modules..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="settings-search" />
            </div>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {categories.map(c => (
              <button key={c} onClick={() => setActiveCategory(c)}
                className={cn("px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-all",
                  activeCategory === c ? "bg-emerald-brand text-white border-emerald-brand" : "bg-white border-gray-200 text-gray-500 hover:border-gray-300")}>
                {c === 'all' ? 'All' : (CATEGORY_LABELS[c] || c.replace(/_/g, ' '))}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(m => (
              <button key={m.module_id} onClick={() => setActiveModule(m.module_id)}
                className="text-left p-4 bg-white border border-gray-border rounded-xl hover:border-emerald-300 hover:shadow-sm transition-all group"
                data-testid={`config-module-${m.module_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-text-primary group-hover:text-emerald-700 transition-colors">{m.name}</p>
                    <p className="text-[10px] text-gray-400 capitalize mt-0.5">{m.category?.replace(/_/g, ' ')} · {m.tier}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-emerald-500 transition-colors" />
                </div>
              </button>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No modules match your search</p>}
        </>
      )}
    </div>
  );
}
